import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { IRtcStats, IVideoRoomTokenRequest } from 'vcs-common';
import { toast } from 'react-toastify';

import Loader from '../components/common/loader';
import { ConnectionQualityPanel } from '../components/common/connection-quality-panel';
import { MediaSettingsModal } from '../components/settings/media-settings-modal';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useFormFactor } from '../utils/formFactor';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useSocketMessages } from '../hooks/useSocketMessages';
import { socket } from '../libs/socket';
import { getOrCreateGroupClientId } from '../utils/groupMeeting';
import {
  useGenerateVideoRoomTokenMutation,
  useLazyGetClientQuery,
  useLazyGetConsultationByCodeQuery,
  useUploadMetricsMutation,
} from '../services/users.api';
import { selectUser } from '../store/auth.slice';
import { setActiveTool } from '../store/meetActiveTool.slice';
import { setDpDgPlay, setDpDgPosition, setDpDgStop } from '../store/dpdg.slice';
import { clearMacClientSelections, setMacClientSelection, setMacDec, setMacSelected } from '../store/mac.slice';
import { IJournalResult } from '../store/types';
import { setWheelSelected } from '../store/wheel.slice';
import { MeetOverlaysBridgeApi } from '../features/meet/MeetOverlaysProvider';
import { MeetModerationBridgeApi } from '../features/meet/MeetModerationProvider';
import { MeetToolsBridgeApi } from '../features/meet/MeetToolsProvider';
import {
  getRegularVideos,
  getScreenShareFeed,
  getScreenShareOwnerDisplayName,
  getSharingPersonName,
  meetBackgrounds,
} from '../features/meet/meet.adapters';
import { useMeetPermissions } from '../features/meet/useMeetPermissions';
import type { MeetChatAuthorContext } from '../features/meet/chat/chat.mappers';
import { useMeetChat } from '../features/meet/chat/useMeetChat';
import { useMeetSession } from '../features/meet/useMeetSession';
import { SupervisorNotification, useMeetSupervisor } from '../features/meet/useMeetSupervisor';
import { useMeetVideoRoomBridge } from '../features/meet/useMeetVideoRoomBridge';
import { PsyToolsId } from '../components/meet/tools-panel/types';
import { CallDebugPanel } from '../components/meet/call-debug-panel';
import { Breakpoint } from '../utils/breakpoint';
import { isGroupConsultation } from '../utils/consultation';

import { MeetJoinFlow } from './meet/meet-join-flow';
import { MeetOverlays } from './meet/meet-overlays';
import { MeetBaseProviderTree, MeetSceneProviderTree } from './meet/meet-provider-tree';
import { MeetRoomScene } from './meet/meet-room-scene';

const createEmptyJournalResult = (): IJournalResult => ({
  questions: [],
  dpdg: { date_time: '', comment: '' },
  emotions: [],
  mac_cards: [],
  wheel: [],
  color_code: [],
  clouds: [],
  result: '',
});

const noop = () => undefined;

const Meet = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const breakpoint = useBreakpoint();
  const formFactor = useFormFactor();

  const roomName = params.id || '';
  const isMobile = breakpoint <= Breakpoint.Sm;

  const [getConsultations, { data: consultation }] = useLazyGetConsultationByCodeQuery();
  const [generateToken] = useGenerateVideoRoomTokenMutation();
  const [uploadMetrics] = useUploadMetricsMutation();
  const [getClient, { data: client }] = useLazyGetClientQuery();

  const userData = useAppSelector(selectUser);
  const macClientSelections = useAppSelector((state) => state.macStateSlice.clientSelectionsByParticipant);
  const emotionsClientSelections = useAppSelector((state) => state.emotionsGroupSlice.clientSelectionsByParticipant);

  const [result, setResult] = useState<IJournalResult>(() => createEmptyJournalResult());
  const [isResultInitialized, setIsResultInitialized] = useState(false);
  const [isPsychologist, setIsPsychologist] = useState(false);
  const [colorCodeClientInbound, setColorCodeClientInbound] = useState<{
    nonce: number;
    selectedColors: string[];
  } | null>(null);
  const [emotionsClientInbound, setEmotionsClientInbound] = useState<{
    nonce: number;
    result: { category: string[]; emo: string[] };
  } | null>(null);
  const [wheelLiveKitEvent, setWheelLiveKitEvent] = useState<{
    nonce: number;
    kind: 'change' | 'result';
    values: number[];
  } | null>(null);
  const [wheelBootstrap, setWheelBootstrap] = useState<{ nonce: number; values: number[] } | null>(null);
  const [psychDpdgInbound, setPsychDpdgInbound] = useState<{
    nonce: number;
    action: string;
    data: Record<string, unknown>;
  } | null>(null);
  const [clientDpdgBootstrap, setClientDpdgBootstrap] = useState<{
    nonce: number;
    initialSync?: Record<string, unknown>;
  } | null>(null);
  const [clientDpdgInbound, setClientDpdgInbound] = useState<{
    nonce: number;
    action: string;
    data: Record<string, unknown>;
  } | null>(null);

  const groupParticipantNamesRef = useRef<Record<string, string>>({});
  const [groupParticipantNames, setGroupParticipantNames] = useState<Record<string, string>>({});
  const onGroupConnectionRequestRef = useRef<(payload: { identity: string; displayName?: string }) => void>(noop);
  const onConnectionAllowedRef = useRef<(identity?: string) => void>(noop);
  const onMutedByHostRef = useRef<() => void>(noop);
  const onVideoStoppedByHostRef = useRef<() => void>(noop);
  const setSupervisorNotificationRef = useRef<(value: SupervisorNotification | null) => void>(() => undefined);
  const setSupervisorSendRef = useRef<(value: boolean) => void>(() => undefined);
  const refetchRecordingStatusRef = useRef<() => void>(noop);
  const emitSessionEndedRef = useRef<() => void>(noop);
  const setIsCoOrganizerRef = useRef<(value: boolean) => void>(() => undefined);
  const toolsApiRef = useRef<MeetToolsBridgeApi | null>(null);
  const overlaysApiRef = useRef<MeetOverlaysBridgeApi | null>(null);
  const moderationApiRef = useRef<MeetModerationBridgeApi | null>(null);
  const onChatMessageRef = useRef<
    (message: {
      id: string;
      kind: 'text' | 'image';
      text: string;
      sentAt: string;
      senderId: string;
      senderName?: string;
      image?: {
        dataUrl: string;
        mimeType: string;
        width: number;
        height: number;
      };
    }) => void
  >(noop);

  const isGroupConsultationFlag = isGroupConsultation(consultation, client);

  // Групповой участник, залогиненный под посторонним аккаунтом (например, психолог другой
  // консультации), для consultation-service не участник этой встречи — с его Authorization
  // generate-token отдаёт 403. Заходить он должен по коду комнаты, как гость.
  // Флаг держим в ref: generateRoomToken уходит внутрь видео-хука, и его пересоздание
  // дёргало бы переподключение к комнате.
  const shouldJoinAsGuestRef = useRef(false);
  shouldJoinAsGuestRef.current = isGroupConsultationFlag && !isPsychologist;

  const generateRoomToken = useCallback(
    async (request: IVideoRoomTokenRequest) => {
      try {
        return await generateToken({ ...request, as_guest: shouldJoinAsGuestRef.current }).unwrap();
      } catch (error: unknown) {
        console.error(`Failed to generate token for room ${request.room_name}, user ${request.user_identity}`, error);

        if (typeof error === 'object' && error !== null && 'status' in error) {
          const status = error.status;

          if (status === 403) {
            toast.error('Консультация не активна, доступ запрещен');
            return;
          }

          if (status === 404) {
            toast.error('Консультация не найдена');
            return;
          }

          if (status === 409) {
            toast.error('В комнате уже максимальное число участников');
            return;
          }
        }

        toast.error('Произошла непредвиденная ошибка при подключении к консультации.');
      }
    },
    [generateToken],
  );

  const onStatsReport = useCallback(
    async (report: IRtcStats) => {
      try {
        return await uploadMetrics(report).unwrap();
      } catch (error) {
        console.error(`Failed to upload metrics for room ${report.roomCode}, user ${report.localUserIdentity}`, error);
      }
    },
    [uploadMetrics],
  );

  const {
    initialized: videoRoomInitialized,
    getRoomId,
    getRoomInfo,
    ensureIdentity,
    joinRoom,
    leaveRoom,
    callDestroyRoom,
    toggleAudio,
    toggleVideo,
    joined,
    localVideo,
    remoteVideos,
    changeAudioDevice,
    changeVideoDevice,
    changeAudioOutput,
    prepareLocalMedia,
    destroyed,
    audioDevice,
    videoDevice,
    stopVideoTrack,
    initializeConnection,
    isLocalActiveSpeaker,
    toggleBackground,
    backgroundMode,
    isBackgroundSupported,
    sendSupervisorRequest,
    sendSupervisorResponse,
    sendRecordingStatusUpdate,
    sendCloudsOpen,
    sendCloudsClose,
    sendCloudsDrawLine,
    sendCloudsClearDrawing,
    sendCloudsSelectImage,
    sendCloudsClientFinished,
    sendCloudsUndo,
    sendMacCardsOpen,
    sendMacSelectCards,
    sendMacCardsClose,
    sendEmotionsOpen,
    sendEmotionsClose,
    sendEmotionsSelect,
    sendColorCodeOpen,
    sendColorCodeClose,
    sendColorCodeResult,
    sendWheelOpen,
    sendWheelClose,
    sendWheelChange,
    sendWheelResult,
    sendDpdgOpen,
    sendDpdgClose,
    sendDpdgMessage,
    sendDpdgVideoControl,
    sendRequestMuteParticipant,
    sendRequestStopVideoParticipant,
    sendRemoveParticipantFromMeeting,
    sendAssignCoOrganizer,
    sendConnectionAllowed,
    sendMacSharedView,
    sendEmotionsSharedView,
    sendSandTherapyOpen,
    sendSandTherapyClose,
    sendSandSelectFigures,
    sendSandStartPlacement,
    sendSandPlaceFigure,
    sendSandMoveFigure,
    sendSandRotateFigure,
    sendSandResizeFigure,
    sendSandRemoveFigure,
    sendSandColorChange,
    sendSandClientReady,
    sendChatMessage,
    startScreenShare,
    stopScreenShare,
    isScreenSharing,
    room,
    connectionQuality,
  } = useMeetVideoRoomBridge({
    roomName,
    isPsychologist,
    isGroupConsultationFlag,
    dispatch,
    navigate,
    generateRoomToken,
    onStatsReport,
    setSupervisorNotificationRef,
    setSupervisorSendRef,
    refetchRecordingStatusRef,
    emitSessionEndedRef,
    setIsCoOrganizerRef,
    onMutedByHostRef,
    onVideoStoppedByHostRef,
    toolsApiRef,
    overlaysApiRef,
    moderationApiRef,
    onChatMessage: (message) => {
      onChatMessageRef.current(message);
    },
    onColorCodeOpen: () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(PsyToolsId.COLOR_CODE));
      }
    },
    onColorCodeClose: () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(0));
      }
    },
    onColorCodeResult: ({ selectedColors }) => {
      if (isPsychologist) {
        setColorCodeClientInbound({ nonce: Date.now(), selectedColors });
      }
    },
    onEmotionsSelect: ({ result: incomingResult }) => {
      if (isPsychologist) {
        setEmotionsClientInbound({ nonce: Date.now(), result: incomingResult });
      }
    },
    onWheelOpen: ({ values }) => {
      if (!isPsychologist) {
        dispatch(setActiveTool(PsyToolsId.WHEEL));
        dispatch(setWheelSelected(values));
        setWheelBootstrap({ nonce: Date.now(), values });
      }
    },
    onWheelChange: ({ values }) => {
      if (isPsychologist) {
        setWheelLiveKitEvent({ nonce: Date.now(), kind: 'change', values });
      }
    },
    onWheelResult: ({ values }) => {
      if (isPsychologist) {
        setWheelLiveKitEvent({ nonce: Date.now(), kind: 'result', values });
      }
    },
    onWheelClose: () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(0));
      }
    },
    onDpdgOpen: ({ initialSync }) => {
      if (!isPsychologist) {
        if (window.innerHeight > window.innerWidth) {
          overlaysApiRef.current?.setShowModalInstruct(
            'Психолог активировал инструмент "ДПДГ онлайн". Пожалуйста, переверните экран телефона в горизонтальное положение',
          );
        }
        dispatch(setActiveTool(PsyToolsId.DPDG));
        setClientDpdgBootstrap({ nonce: Date.now(), initialSync });
      }
    },
    onDpdgClose: () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(0));
        dispatch(setDpDgStop());
      }
    },
    onDpdgMessage: (action, data) => {
      if (isPsychologist) {
        setPsychDpdgInbound({ nonce: Date.now(), action, data });
      } else {
        setClientDpdgInbound({ nonce: Date.now(), action, data });
      }
    },
    onDpdgVideoControl: (payload) => {
      if (!isPsychologist) {
        if (payload.type === 'pause') {
          dispatch(setDpDgStop());
        } else if (payload.type === 'play') {
          if (typeof payload.time === 'number') {
            dispatch(setDpDgPosition(payload.time));
          }
          dispatch(setDpDgPlay());
        } else if (payload.type === 'seek') {
          dispatch(setDpDgPosition(payload.time ?? 0));
        }
      }
    },
  });

  const permissions = useMeetPermissions({
    roomName,
    audioDevice: audioDevice ?? undefined,
    videoDevice: videoDevice ?? undefined,
    toggleAudio,
    toggleVideo,
  });

  const supervisor = useMeetSupervisor({
    consultationId: consultation?.id,
    roomName,
    scheduledEnd: consultation?.datetime_end,
    isMobile,
    isGroupConsultationFlag,
    isPsychologist,
    sendSupervisorRequest,
    sendRecordingStatusUpdate,
  });

  const session = useMeetSession({
    roomName,
    consultation,
    client,
    userData,
    isPsychologist,
    isGroupConsultationFlag,
    destroyed,
    videoRoomInitialized,
    joinRoom,
    leaveRoom,
    callDestroyRoom,
    getRoomId,
    getRoomInfo,
    initializeConnection,
    refreshPermissions: permissions.refreshPermissions,
    navigate,
    result,
    setResult,
    isResultInitialized,
    setIsResultInitialized,
    groupParticipantNamesRef,
    macClientSelections,
    emotionsClientSelections,
  });

  useEffect(() => {
    permissions.setVideoMuted(session.isInitialVideoMuted);
  }, [permissions.setVideoMuted, session.isInitialVideoMuted]);

  // Групповой клиент должен быть в комнате под своим постоянным identity уже в лобби:
  // по нему психолог адресует разрешение входа, и по нему же участник опознаётся,
  // когда действительно подключится.
  useEffect(() => {
    if (!isGroupConsultationFlag || isPsychologist || !videoRoomInitialized || joined || !roomName) {
      return;
    }

    void ensureIdentity?.(getOrCreateGroupClientId(roomName).identity);
  }, [ensureIdentity, isGroupConsultationFlag, isPsychologist, joined, roomName, videoRoomInitialized]);

  onGroupConnectionRequestRef.current = (payload) => {
    moderationApiRef.current?.onGroupConnectionRequest(payload);
  };
  onConnectionAllowedRef.current = (identity) => {
    moderationApiRef.current?.handleConnectionAllowed(identity);
  };
  onMutedByHostRef.current = permissions.handleMutedByHost;
  onVideoStoppedByHostRef.current = permissions.handleVideoStoppedByHost;
  setSupervisorNotificationRef.current = supervisor.setSupervisorNotification;
  setSupervisorSendRef.current = supervisor.setSupervisorSend;
  refetchRecordingStatusRef.current = supervisor.refetchRecordingStatus;
  emitSessionEndedRef.current = session.emitSessionEnded;
  setIsCoOrganizerRef.current = (value) => {
    moderationApiRef.current?.setIsCoOrganizer(value);
  };

  useEffect(() => {
    getConsultations(String(roomName));
  }, [getConsultations, roomName]);

  useEffect(() => {
    if (!consultation?.client_entity) {
      return;
    }

    const clientId = consultation.client_entity.split('/').at(-1);

    if (!clientId) {
      return;
    }

    if (isPsychologist || consultation.is_group) {
      getClient(clientId);
    }
  }, [consultation, getClient, isPsychologist]);

  useEffect(() => {
    if (!userData || !consultation) {
      setIsPsychologist(false);
      return;
    }

    setIsPsychologist(userData.id === consultation.psychologist.id);
  }, [consultation, userData]);

  useEffect(() => {
    if (remoteVideos.length > 0) {
      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'micro_client',
          data: permissions.isAudioMuted,
        },
      });
    }
  }, [permissions.isAudioMuted, remoteVideos, roomName]);

  const localParticipantIdentity = room?.localParticipant?.identity ?? '';
  const screenShareFeed = getScreenShareFeed(remoteVideos);
  const screenShareOwnerName = getScreenShareOwnerDisplayName(screenShareFeed, getRegularVideos(remoteVideos));
  const localChatDisplayName = useMemo(() => {
    const psychologistName = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (psychologistName) {
      return psychologistName;
    }

    if (client?.full_name) {
      return client.full_name;
    }

    return localParticipantIdentity;
  }, [client?.full_name, localParticipantIdentity, userData?.first_name, userData?.last_name, userData?.middle_name]);

  const chatAuthorContext = useMemo<MeetChatAuthorContext>(
    () => ({
      isGroupConsultation: isGroupConsultationFlag,
      isPsychologist,
      localParticipantId: localParticipantIdentity,
      psychologistId: consultation?.psychologist?.id,
      participantDisplayNames: groupParticipantNames,
    }),
    [
      consultation?.psychologist?.id,
      groupParticipantNames,
      isGroupConsultationFlag,
      isPsychologist,
      localParticipantIdentity,
    ],
  );

  const chat = useMeetChat({
    localParticipantId: localParticipantIdentity,
    localParticipantName: localChatDisplayName,
    authorContext: chatAuthorContext,
    sendMessage: sendChatMessage,
  });

  onChatMessageRef.current = chat.handleIncomingMessage;

  useSocketMessages({
    setIsAudioClientMute: () => undefined,
    setIsVideoClientMute: () => undefined,
    onStopScreenShare: () => {
      if (isScreenSharing) {
        stopScreenShare?.();
      }
    },
    onGroupConnectionRequest: (payload) => {
      if (moderationApiRef.current?.canManageParticipants) {
        moderationApiRef.current.onGroupConnectionRequest(payload);
      }
    },
    onConnectionAllowed: isGroupConsultationFlag
      ? (data) => {
          moderationApiRef.current?.handleConnectionAllowed(data?.for);
        }
      : undefined,
    onRaisedHandChange: isGroupConsultationFlag
      ? (data) => {
          moderationApiRef.current?.handleRaisedHandChange(data);
        }
      : undefined,
  });

  const handleSettingsOpenChange = useCallback(
    async (value: boolean) => {
      stopVideoTrack?.();

      try {
        await prepareLocalMedia?.();
      } catch (error) {
        console.error('Failed to prepare local media for settings', error);
      }

      overlaysApiRef.current?.setOpenSettings(value);
    },
    [prepareLocalMedia, stopVideoTrack],
  );

  const changeSettings = useCallback(
    async (
      video: MediaDeviceInfo | undefined,
      audioOut: MediaDeviceInfo | undefined,
      audioIn: MediaDeviceInfo | undefined,
    ) => {
      await changeAudioDevice?.(audioIn, permissions.isAudioMuted);
      await changeVideoDevice?.(video, permissions.isVideoMuted);

      if (audioOut?.deviceId) {
        changeAudioOutput(audioOut);
      }
    },
    [changeAudioDevice, changeAudioOutput, changeVideoDevice, permissions.isAudioMuted, permissions.isVideoMuted],
  );

  const handleScreenShareClick = useCallback(() => {
    if (isScreenSharing) {
      stopScreenShare?.();
      return;
    }

    if (getScreenShareFeed(moderationApiRef.current?.remoteVideosForMeeting ?? [])) {
      overlaysApiRef.current?.setShowScreenShareConflictModal(true);
      return;
    }

    startScreenShare?.();
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  const handleConfirmScreenShare = useCallback(() => {
    overlaysApiRef.current?.setShowScreenShareConflictModal(false);

    socket.emit('message', {
      roomId: roomName,
      message: {
        datetime: new Date().toISOString(),
        action: 'stop_screen_share',
      },
    });

    setTimeout(() => {
      startScreenShare?.();
    }, 300);
  }, [roomName, startScreenShare]);

  const handleCancelScreenShare = useCallback(() => {
    overlaysApiRef.current?.setShowScreenShareConflictModal(false);
  }, []);

  const settingsMediaModal = (
    <MediaSettingsModal
      isPsychologist={isPsychologist}
      changeSettings={changeSettings}
      audioDevice={audioDevice}
      videoDevice={videoDevice}
      setOpenSettings={handleSettingsOpenChange}
    />
  );

  const closeActiveTool = useCallback(() => {
    dispatch(setActiveTool(0));
  }, [dispatch]);

  const openGroupMacCards = useCallback(() => {
    dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
    toolsApiRef.current?.setToolsMenu(PsyToolsId.MAC_CARDS);
    toolsApiRef.current?.setMacGroupDeckShown(false);
  }, [dispatch]);

  const openGroupEmotions = useCallback(() => {
    dispatch(setActiveTool(PsyToolsId.EMOTIONS));
    toolsApiRef.current?.setToolsMenu(PsyToolsId.EMOTIONS);
    toolsApiRef.current?.setEmotionsGroupResultsShown(false);
  }, [dispatch]);

  const handleParticipantNamesChange = useCallback((names: Record<string, string>) => {
    groupParticipantNamesRef.current = names;
    setGroupParticipantNames(names);
  }, []);

  const handleModerationApi = useCallback((api: MeetModerationBridgeApi) => {
    moderationApiRef.current = api;
  }, []);

  const handleOverlaysApi = useCallback((api: MeetOverlaysBridgeApi) => {
    overlaysApiRef.current = api;
  }, []);

  const handleToolsApi = useCallback((api: MeetToolsBridgeApi) => {
    toolsApiRef.current = api;
  }, []);

  if (!roomName || !consultation || !getRoomId) {
    return <Loader className="backdrop-blur" />;
  }

  if (!joined) {
    return (
      <>
        <CallDebugPanel />
        <MeetBaseProviderTree
          roomName={roomName}
          consultation={consultation}
          client={client}
          isGroupConsultationFlag={isGroupConsultationFlag}
          isPsychologist={isPsychologist}
          joined={joined}
          localParticipantIdentity={localParticipantIdentity}
          sendConnectionAllowed={sendConnectionAllowed}
          remoteVideos={remoteVideos}
          audioDevice={audioDevice ?? undefined}
          videoDevice={videoDevice ?? undefined}
          toggleAudio={toggleAudio}
          toggleVideo={toggleVideo}
          permissionsValue={permissions}
          onParticipantNamesChange={handleParticipantNamesChange}
          onModerationApi={handleModerationApi}
          onOverlaysApi={handleOverlaysApi}
        >
          <MeetJoinFlow
            consultation={consultation}
            client={client}
            users={session.users}
            isPsychologist={isPsychologist}
            isGroupConsultationFlag={isGroupConsultationFlag}
            videoRoomInitialized={Boolean(videoRoomInitialized)}
            settingsModal={settingsMediaModal}
            startWithMedia={session.startWithMedia}
            onOpenSettingsChange={handleSettingsOpenChange}
          />
        </MeetBaseProviderTree>
      </>
    );
  }

  return (
    <>
      <CallDebugPanel />
      {connectionQuality !== 'GOOD' && (
        <div className="fixed right-4 top-4 z-50 rounded p-1" title={`Качество связи: ${connectionQuality}`}>
          <ConnectionQualityPanel quality={connectionQuality === 'POOR' ? 'poor' : 'unstable'} />
        </div>
      )}
      <MeetSceneProviderTree
        roomName={roomName}
        consultation={consultation}
        client={client}
        isGroupConsultationFlag={isGroupConsultationFlag}
        isPsychologist={isPsychologist}
        joined={joined}
        localParticipantIdentity={localParticipantIdentity}
        sendConnectionAllowed={sendConnectionAllowed}
        remoteVideos={remoteVideos}
        audioDevice={audioDevice ?? undefined}
        videoDevice={videoDevice ?? undefined}
        toggleAudio={toggleAudio}
        toggleVideo={toggleVideo}
        permissionsValue={permissions}
        onParticipantNamesChange={handleParticipantNamesChange}
        onModerationApi={handleModerationApi}
        onOverlaysApi={handleOverlaysApi}
        result={result}
        setResult={setResult}
        handleToolActivated={session.handleToolActivated}
        onToolsApi={handleToolsApi}
      >
        <MeetRoomScene
          session={{
            roomName,
            consultation,
            client,
            users: session.users,
            joined,
            localVideo: localVideo ?? null,
            localParticipantIdentity,
            isPsychologist,
            isClientConnected: session.isClientConnected,
            isLocalActiveSpeaker,
          }}
          layout={{
            breakpoint,
            formFactor,
            hideToolsPanelForGroupCall: isGroupConsultationFlag,
          }}
          controls={{
            closeMeet: session.closeMeet,
            leaveConferenceOnly: session.leaveConferenceOnly,
            closeActiveTool,
            onOpenSettingsClick: () => {
              overlaysApiRef.current?.setOpenSettings((prev) => !prev);
            },
            onBackgroundBlurClick: () => {
              toggleBackground('blur');
            },
            onSupervisorClick: supervisor.handleSupervisorClick,
            onScreenShareClick: handleScreenShareClick,
            onOpenMacDeckModal: () => overlaysApiRef.current?.setMacDeckModalOpen(true),
            onOpenMacCardsForGroup: openGroupMacCards,
            onOpenEmotionsForGroup: openGroupEmotions,
            onToggleRaisedHand: () => moderationApiRef.current?.toggleRaisedHand?.(),
          }}
          supervisor={{
            statusActive: supervisor.statusData?.active || false,
            isSupervisorAllowed: supervisor.isSupervisorAllowed,
            supervisorRemaining: supervisor.supervisorRemaining,
            isSupervisorBalanceWarning: supervisor.isSupervisorBalanceWarning,
            isTooltipVisible: supervisor.isTooltipVisible,
            supervisorNotification: supervisor.supervisorNotification,
            setSupervisorNotification: supervisor.setSupervisorNotification,
            supervisorSend: supervisor.supervisorSend,
            setSupervisorSend: supervisor.setSupervisorSend,
            notificationClosed: supervisor.notificationClosed,
            setNotificationClosed: supervisor.setNotificationClosed,
            isSupervisorLoading: supervisor.isSupervisorLoading,
          }}
          videoRoomActions={{
            backgroundMode,
            isBackgroundSupported,
            toggleBackground,
            stopVideoTrack,
            sendSupervisorResponse,
            sendMacCardsOpen,
            sendMacCardsClose,
            sendMacSelectCards,
            sendCloudsOpen,
            sendCloudsClose,
            sendCloudsDrawLine,
            sendCloudsClearDrawing,
            sendCloudsSelectImage,
            sendCloudsClientFinished,
            sendCloudsUndo,
            sendEmotionsOpen,
            sendEmotionsClose,
            sendEmotionsSelect,
            sendColorCodeOpen,
            sendColorCodeClose,
            sendColorCodeResult,
            sendWheelOpen,
            sendWheelClose,
            sendWheelChange,
            sendWheelResult,
            sendDpdgOpen,
            sendDpdgClose,
            sendDpdgMessage,
            sendDpdgVideoControl,
            sendRequestMuteParticipant,
            sendRequestStopVideoParticipant,
            sendRemoveParticipantFromMeeting,
            sendAssignCoOrganizer,
            sendMacSharedView,
            sendEmotionsSharedView,
            sendSandTherapyOpen,
            sendSandTherapyClose,
            sendSandSelectFigures,
            sendSandStartPlacement,
            sendSandPlaceFigure,
            sendSandMoveFigure,
            sendSandRotateFigure,
            sendSandResizeFigure,
            sendSandRemoveFigure,
            sendSandColorChange,
            sendSandClientReady,
            stopScreenShare,
            isScreenSharing,
          }}
          chat={{
            isOpen: chat.isOpen,
            draft: chat.draft,
            messages: chat.messages,
            isSendingImage: chat.isSendingImage,
            hasUnread: chat.hasUnread,
            disabled: !sendChatMessage || !localParticipantIdentity,
            onDraftChange: chat.setDraft,
            onSend: chat.send,
            onImageSelect: chat.sendImage,
            onToggle: chat.toggleOpen,
          }}
          toolInbound={{
            colorCodeClientInbound,
            onColorCodeClientInboundConsumed: () => setColorCodeClientInbound(null),
            emotionsClientInbound,
            onEmotionsClientInboundConsumed: () => setEmotionsClientInbound(null),
            wheelLiveKitEvent,
            onWheelLiveKitEventConsumed: () => setWheelLiveKitEvent(null),
            wheelBootstrap,
            onWheelBootstrapConsumed: () => setWheelBootstrap(null),
            psychDpdgInbound,
            onPsychDpdgInboundConsumed: () => setPsychDpdgInbound(null),
            clientDpdgBootstrap,
            onClientDpdgBootstrapConsumed: () => setClientDpdgBootstrap(null),
            clientDpdgInbound,
            onClientDpdgInboundConsumed: () => setClientDpdgInbound(null),
          }}
        />
        <MeetOverlays
          settingsModal={settingsMediaModal}
          isPsychologist={isPsychologist}
          backgrounds={meetBackgrounds}
          showMobilePopup={supervisor.showMobilePopup}
          showDesktopModal={supervisor.showDesktopModal}
          showMobilePopupStartSupervisor={supervisor.showMobilePopupStartSupervisor}
          showDesktopModalStartSupervisor={supervisor.showDesktopModalStartSupervisor}
          sharingPersonName={getSharingPersonName(
            isPsychologist,
            consultation?.psychologist?.first_name,
            client?.full_name,
            screenShareOwnerName,
          )}
          onConfirmMacDeckModal={() => {
            dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
            toolsApiRef.current?.setToolsMenu(PsyToolsId.MAC_CARDS);
          }}
          sendMacCardsOpen={sendMacCardsOpen}
          onCloseInstruction={() => overlaysApiRef.current?.setShowModalInstruct(null)}
          onCloseBackgroundModal={() => overlaysApiRef.current?.setShowBackgroundModal(false)}
          onBlurBackground={() => {
            toggleBackground('blur');
            overlaysApiRef.current?.setShowBackgroundModal(false);
          }}
          onSelectBackground={(src) => {
            toggleBackground('image', src);
            overlaysApiRef.current?.setShowBackgroundModal(false);
          }}
          onRejectStopSupervisor={supervisor.rejectStopSupervisor}
          onConfirmStopSupervisor={supervisor.confirmStopSupervisor}
          onRejectStartSupervisorGroup={supervisor.rejectStartSupervisorGroup}
          onConfirmStartSupervisorGroup={supervisor.confirmStartSupervisorGroup}
          onConfirmScreenShare={handleConfirmScreenShare}
          onCancelScreenShare={handleCancelScreenShare}
        />
      </MeetSceneProviderTree>
    </>
  );
};

export default Meet;
